// Der Datenvertrag fürs Büro (E3, Schritt 1 – PLAN.md, E3, „Handwerk“): was
// das CRM am Laptop an den Server schickt – Freigabe und Material, die
// Standardliste, Berichtigen, Montagetermin, Jahresziel, „gesehen“ – und die Fragen mit
// Zusammenhang (Grundsatz 9), die aus beiden Apps kommen. Geprüft auf beiden
// Seiten mit denselben Regeln, wie aufnahme-vertrag.ts; Kennung, Zeitpunkt,
// Name und Adresse kommen von dort (eine Quelle).
//
// Grundregeln, wie bei der Besichtigung:
//  - Kennungen entstehen am Gerät (UUID) – auch im Browser des Büros: eine
//    Frage, eine Antwort, eine Materialposition, ein Eintrag der
//    Standardliste. So ist ein zweites Senden (Netz weg, Knopf doppelt)
//    harmlos.
//  - Fragen und Antworten sind Kundendaten (Löschregel O19, Löschknopf E4,
//    Verarbeitungsverzeichnis O30). Der Verlauf bekommt davon nur Kennungen.
//  - Mengen, Meter und Preise gehen als Zahl über die Leitung; die Datenbank
//    hält sie genau (numeric, zwei Nachkommastellen).
//
// Seit Teil 2 von Schritt 1 stehen unten auch die Antworten der Wege fürs
// Büro (`/api/crm/…`, server/src/crm/routen.ts): die Liste „zu prüfen“, eine
// Besichtigung ganz, die Fragen mit ihrem Zusammenhang. Seit Teil 3 dazu,
// was die übrigen Seiten lesen: Manuels Übersicht mit echten Zahlen (S2),
// die Unterlagen für die Montage (S4), Kunden und Anlagen und die Akte einer
// Anlage (S5). Die Materiallisten fürs Lager gab es bis E3c – ein Lager gibt
// es nicht mehr (H26).
//
// Die Felder von Frage, Antwort und Montagetermin gibt es auch einzeln
// (`frageFelder`, `antwortFelder`, `montageterminFelder`): Die Doku-App
// schickt dieselben Dinge als Einträge über ihren Eingang
// (aufnahme-vertrag.ts) – eine Regel, zwei Türen.

import * as z from "zod";
import type { AkteAblauf } from "./ablauf-vertrag.ts";
import type { AufnahmeAmServer, AufnahmeStandAmServer, EintragAbweisung } from "./aufnahme-vertrag.ts";
import type { Kette } from "./kette.ts";
import { freiwillig, kennung, kundeFelder, namensFelder, objektFelder, pflicht, zeitpunkt } from "./vertrag-bausteine.ts";

/** Die Grenzen des Vertrags – an einer Stelle, damit App und Server dieselben kennen. */
export const BUERO_GRENZEN = {
  /** Text einer Frage oder Antwort */
  frageText: 2000,
  /** Der Text zum Montagetermin („Partie 2, 3 Tage“) */
  montageterminText: 200,
  /** Positionen je Freigabe */
  positionen: 500,
  menge: 99_999,
  /** Preis je Position – noch ohne Funktion (DECISIONS.md, 12.10.); die Grenze schützt nur die Spalte. */
  preis: 9_999_999,
  artikelnummer: 60,
  bezeichnung: 200,
  einheit: 20,
  /** Die Gruppe eines Eintrags der Standardliste („Kabelkanal“) – seit E3b, für Varianten. */
  gruppe: 60,
} as const;

// --- Feste Wertelisten (die Datenbank prüft sie mit – server/src/db/schema.ts) -----

/** Woran eine Frage hängt: am ganzen Projekt, an der Besichtigung, einer Station, einem Foto, einer Notiz – später auch Material und Montage. */
export const FRAGE_BEZUEGE = ["anlage", "aufnahme", "station", "foto", "notiz"] as const;
export type FrageBezug = (typeof FRAGE_BEZUEGE)[number];

export const FRAGE_STAENDE = ["offen", "erledigt"] as const;
export type FrageStand = (typeof FRAGE_STAENDE)[number];

/** Ein Eintrag der Standardliste: immer dabei oder zur Auswahl beim Ergänzen. */
export const MATERIAL_ARTEN = ["immer", "auswahl"] as const;
export type MaterialArt = (typeof MATERIAL_ARTEN)[number];

/** Woher eine Materialposition kommt: aus der Standardliste, aus der Aufnahme (Längen), vom Büro ergänzt. */
export const MATERIAL_HERKUENFTE = ["standard", "aufnahme", "buero"] as const;
export type MaterialHerkunft = (typeof MATERIAL_HERKUENFTE)[number];

// --- Bausteine ---------------------------------------------------------------------

/** Ein Kalendertag „JJJJ-MM-TT“ ohne Uhrzeit – und einer, den es gibt (kein 30. Februar). */
export const kalendertag = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum als JJJJ-MM-TT.")
  .refine(istKalendertag, "Diesen Tag gibt es nicht.");
export type Kalendertag = z.infer<typeof kalendertag>;

/** Gibt es diesen Tag? Rechnet über UTC, damit keine Zeitzone den Tag verschiebt. */
function istKalendertag(text: string): boolean {
  const [jahr, monat, tag] = text.split("-").map(Number) as [number, number, number];
  const d = new Date(Date.UTC(jahr, monat - 1, tag));
  // Date.UTC legt Jahre unter 100 ins 20. Jahrhundert – dann stimmt das Jahr nicht und der Tag zählt nicht.
  return d.getUTCFullYear() === jahr && d.getUTCMonth() === monat - 1 && d.getUTCDate() === tag;
}

/** Der Text einer Frage oder Antwort: getrimmt, nicht leer, nicht endlos. */
const frageText = (meldung: string) =>
  z.string().trim().min(1, meldung).max(BUERO_GRENZEN.frageText, `Höchstens ${BUERO_GRENZEN.frageText} Zeichen.`);

const menge = z
  .number()
  .min(0, "Die Menge kann nicht negativ sein.")
  .max(BUERO_GRENZEN.menge, `Höchstens ${BUERO_GRENZEN.menge}.`);

/**
 * Ein Material, wie Menschen es tippen: Artikelnummer freiwillig, Bezeichnung
 * und Einheit Pflicht. Seit E3b auch für das Material, das der Verkäufer
 * draußen angibt (aufnahme-vertrag.ts, Eintrag „material“) – dieselben Regeln.
 */
export const materialFelder = {
  artikelnummer: freiwillig(BUERO_GRENZEN.artikelnummer),
  bezeichnung: pflicht(BUERO_GRENZEN.bezeichnung, "Bitte eine Bezeichnung eingeben."),
  menge,
  einheit: pflicht(BUERO_GRENZEN.einheit, "Bitte eine Einheit eingeben (z. B. Stk, m)."),
};

// --- Fragen mit Zusammenhang (Grundsatz 9) -------------------------------------------

/** Was eine Frage trägt – im CRM (`frageEingabe`) und als Eintrag der Doku-App (Art „frage“). */
export const frageFelder = {
  bezug: z.object({ tabelle: z.enum(FRAGE_BEZUEGE), id: kennung }),
  text: frageText("Bitte eine Frage eingeben."),
  gestelltAm: zeitpunkt,
};

/** Eine Frage an der Besichtigung selbst trägt deren Kennung als Bezug. */
export function frageAnDerBesichtigung(f: { aufnahmeId: string; bezug: { tabelle: FrageBezug; id: string } }): boolean {
  return f.bezug.tabelle !== "aufnahme" || f.bezug.id === f.aufnahmeId;
}
export const FRAGE_AN_DER_BESICHTIGUNG = { message: "Eine Frage an der Besichtigung trägt deren Kennung.", path: ["bezug", "id"] };

/**
 * Eine Frage – an einem Stück der Besichtigung (Bezug: Tabelle und Kennung,
 * wie der Verlauf). `aufnahmeId` ist die Besichtigung, in deren
 * Zusammenhang gefragt wird: der Anker für die Listen, den Hinweis in der
 * Doku-App und das Löschen mit der Aufnahme. Ob das Stück wirklich zu ihr
 * gehört, prüft der Server.
 */
export const frageEingabe = z
  .object({
    id: kennung,
    aufnahmeId: kennung,
    ...frageFelder,
  })
  .refine(frageAnDerBesichtigung, FRAGE_AN_DER_BESICHTIGUNG);
export type FrageEingabe = z.infer<typeof frageEingabe>;

/** Was eine Antwort trägt – im CRM (`antwortEingabe`) und als Eintrag der Doku-App (Art „antwort“). */
export const antwortFelder = {
  frageId: kennung,
  text: frageText("Bitte eine Antwort eingeben."),
  geantwortetAm: zeitpunkt,
};

export const antwortEingabe = z.object({
  id: kennung,
  ...antwortFelder,
});
export type AntwortEingabe = z.infer<typeof antwortEingabe>;

/** „Erledigt“ – oder wieder „offen“. */
export const frageStandEingabe = z.object({
  frageId: kennung,
  stand: z.enum(FRAGE_STAENDE),
});
export type FrageStandEingabe = z.infer<typeof frageStandEingabe>;

// --- Montagetermin (Gernots Entscheidung 4) --------------------------------------------

/**
 * Der Montagetermin, mit dem Kunden vereinbart: ein Tag und ein Text
 * („Partie 2, 3 Tage“). Kein Kalender, keine Partie-Planung (Kiste Termin).
 * Beides null: kein Termin.
 */
export const montageterminFelder = {
  am: kalendertag.nullable(),
  text: freiwillig(BUERO_GRENZEN.montageterminText),
};
export const montageterminEingabe = z.object(montageterminFelder);
export type MontageterminEingabe = z.infer<typeof montageterminEingabe>;

// --- Material: Freigabe und Änderung nach der Freigabe -------------------------------------

/**
 * Eine Materialposition, wie das Büro sie freigibt. Die Kennung entsteht im
 * Browser des Büros (dort lebt der Entwurf bis zum Freigeben). Positionen
 * sind Kopien, keine Verweise auf die Standardliste: Ändert das Büro die
 * Standardliste, bleibt die Materialliste, wie sie freigegeben wurde.
 * `stationId`: bei Positionen aus der Aufnahme die Station der Längen.
 * Die Menge wird nicht gerundet – die Datenbank hält zwei Nachkommastellen.
 */
export const positionEingabe = z.object({
  id: kennung,
  herkunft: z.enum(MATERIAL_HERKUENFTE),
  stationId: kennung.nullable(),
  ...materialFelder,
  preis: z
    .number()
    .min(0, "Der Preis kann nicht negativ sein.")
    .max(BUERO_GRENZEN.preis, `Höchstens ${BUERO_GRENZEN.preis}.`)
    .nullable(),
});
export type PositionEingabe = z.infer<typeof positionEingabe>;

/** „Freigeben“: alle Positionen auf einmal, jede Kennung genau einmal. */
export const freigabeEingabe = z.object({
  positionen: z
    .array(positionEingabe)
    .max(BUERO_GRENZEN.positionen, `Höchstens ${BUERO_GRENZEN.positionen} Positionen.`)
    .refine((liste) => new Set(liste.map((p) => p.id)).size === liste.length, "Jede Position mit eigener Kennung."),
});
export type FreigabeEingabe = z.infer<typeof freigabeEingabe>;

/**
 * „Material nach der Freigabe ändern“ – dieselbe Form wie die Freigabe: die
 * ganze Liste, wie sie jetzt gelten soll. Der Server überschreibt nichts:
 * Eine geänderte Position bekommt `ersetzt_am`, die neue nennt ihre
 * Vorgängerin (Gernots Entscheidung 5) – so bleibt nachvollziehbar, was
 * vorher galt.
 *
 * `grundlage` (seit der Code-Prüfung E3, Befund 1): die Kennungen der
 * Positionen, die galten, als die Änderung im Browser begann. Gilt am
 * Server inzwischen etwas anderes – jemand anderer hat dazwischen geändert
 * –, weist er die ganze Liste ab (409 „konflikt“), statt still dessen
 * Positionen zu entfernen.
 */
export const materialEingabe = freigabeEingabe.extend({
  grundlage: z.array(kennung).max(BUERO_GRENZEN.positionen),
});
export type MaterialEingabe = z.infer<typeof materialEingabe>;

/**
 * Eine Menge oder ein Preis, wie die Datenbank sie hält: auf zwei
 * Nachkommastellen, ab der Hälfte aufgerundet (1,005 → 1,01). Der Server
 * rundet so, BEVOR er schreibt – dann steht in der Datenbank genau diese
 * Zahl, und „hat sich die Menge geändert?“ ist ein einfacher Vergleich
 * (Material nach der Freigabe, Standardliste). Gerechnet über den Text,
 * nicht über `zahl * 100`: 1,005 × 100 ist im Rechner 100,4999…
 */
export function aufZweiStellen(zahl: number): number {
  return Number(`${Math.round(Number(`${zahl.toFixed(10)}e2`))}e-2`);
}

// --- Die Standardliste ----------------------------------------------------------------------

/**
 * Ein Eintrag der Standardliste, wie das Büro ihn pflegt. Die Produktlinie
 * ist in Stufe 1 fest „sun“. Einträge werden inaktiv gesetzt, nie gelöscht.
 *
 * `gruppe` (seit E3b, O65): Einträge mit derselben Gruppe sind draußen EINE
 * Kachel mit Varianten („Kabelkanal“ → 40×60, 60×60, 80×100). Freiwillig:
 * Fehlt sie (eine Seite mit altem Stand), bleibt die gespeicherte, wie sie
 * ist; leer heißt „keine Gruppe“.
 */
export const standardmaterialEingabe = z.object({
  id: kennung,
  ...materialFelder,
  art: z.enum(MATERIAL_ARTEN),
  aktiv: z.boolean(),
  gruppe: freiwillig(BUERO_GRENZEN.gruppe).optional(),
});
export type StandardmaterialEingabe = z.infer<typeof standardmaterialEingabe>;

// --- Berichtigen in der Akte (Gernot, 20.10.2026) ---------------------------------------------
// Name und Adresse berichtigt nach dem Senden das Büro; der Verkäufer meldet
// einen Fehler über eine Frage. Dieselben Regeln wie beim Anlegen draußen.

/**
 * Kunde berichtigen im Büro. Seit E3b (O82) mit Vor- und Nachname: Das
 * Formular schickt sie, `name` setzt dann der Server zusammen (name.ts). Eine
 * Seite mit altem Stand schickt nur `name` – das geht weiter; dann gilt der
 * Name ungeteilt. Eins von beiden muss da sein.
 */
export const kundeBerichtigenEingabe = z
  .object({
    ...kundeFelder,
    name: kundeFelder.name.optional(),
    ...namensFelder,
  })
  .refine((e) => Boolean(e.nachname) || Boolean(e.name), { message: "Bitte einen Nachnamen eingeben.", path: ["nachname"] });
export type KundeBerichtigenEingabe = z.infer<typeof kundeBerichtigenEingabe>;

export const objektBerichtigenEingabe = z.object(objektFelder);
export type ObjektBerichtigenEingabe = z.infer<typeof objektBerichtigenEingabe>;

// --- „Anlage löschen“ (seit E3c, S6; PROJEKT.md, O97) ----------------------------------------
//
//   POST  /api/crm/anlage/:id/loeschen   anlageLoeschenEingabe   (Recht „anlage-loeschen“ – nur die Leitung)
//
// Weg ist danach die Anlage samt Besichtigungen, Stationen, Fotos, Notizen,
// Memos, Längen, Material, Materialliste, Fragen und Antworten, Bestellungen
// und Einsätzen – und Kunde und Objekt, wenn nichts anderes an ihnen hängt
// (eine zweite Anlage am Objekt, ein zweites Objekt am Kunden). Die Regel
// steht EINMAL in der Datenbank (Funktion anlage_loeschen, Migration 0011) –
// für den Knopf im CRM, das Verwerfen vom Gerät und das Reserve-Werkzeug am
// Server. Der Verlauf bleibt (nur Kennungen und Zeitpunkte, keine Namen) und
// bekommt eine Zeile „gelöscht“; Fotos und Memos räumt ein täglicher
// Aufräumer nach 24 Stunden weg, wenn sie nirgends mehr hängen. Was gelöscht
// ist, bekommt einen Grabstein: Kein Eintrag vom Gerät legt es wieder an.

/**
 * Die Rückfrage: der Name des Kunden, genau so, wie das CRM ihn zeigt –
 * sonst 409 „konflikt“, ohne den gespeicherten Namen zu verraten (eine
 * falsch abgetippte Kennung träfe sonst einen fremden Kunden).
 */
export const anlageLoeschenEingabe = z.object({
  name: z.string().trim().min(1, "Bitte den Namen des Kunden eingeben.").max(200),
});
export type AnlageLoeschenEingabe = z.infer<typeof anlageLoeschenEingabe>;

/** Die Antwort: ob Kunde und Objekt mitgegangen sind (sonst bleiben sie, weil noch etwas an ihnen hängt). */
export type AnlageLoeschenAntwort = { ok: true; kundeWeg: boolean; objektWeg: boolean } | BueroAbweisung;

/**
 * Was „Anlage löschen“ mitnähme – für den Satz in der Rückfrage: Bleibt der
 * Kunde (er hat noch ein anderes Objekt, oder das Objekt bleibt), bleibt das
 * Objekt (eine zweite Anlage hängt daran, oder es ist Gebäude eines anderen)?
 * Dieselbe Regel wie in der Datenbank (anlage_loeschen).
 */
export interface LoeschFolgen {
  kundeBleibt: boolean;
  objektBleibt: boolean;
}

/**
 * An der Anlage: der Montagetermin, dazu „Auftrag am“ und „montiert am“ für
 * Manuels Zähler „verkauft – montiert – Ziel“ (O58) – von Hand gesetzt, bis
 * die Kisten Sales und Montage sie füllen. Nur, was mitkommt, ändert sich;
 * null löscht den Tag. Mindestens ein Feld.
 */
export const anlageBerichtigenEingabe = z
  .object({
    montagetermin: montageterminEingabe.optional(),
    auftragAm: kalendertag.nullable().optional(),
    montiertAm: kalendertag.nullable().optional(),
    /**
     * Seit E3b (O70): die Dachplanung im Büro anhängen – das K2-PDF, vorher
     * über PUT /api/crm/datei/:pruefsumme hochgeladen (nur PDF). Kein
     * „bitte ansehen“ dafür: Das Büro hat es selbst angehängt.
     */
    dachplanung: z.object({ pruefsumme: z.string().regex(/^[0-9a-f]{64}$/, "Keine gültige Prüfsumme.") }).optional(),
    /**
     * Seit E3c (S3, Prüfung des Plans E3c, Befund 11): der Excel-Report von K2
     * (die Unterkonstruktionsliste) – wie das PDF vorher hochgeladen, nur .xlsx.
     */
    k2Excel: z.object({ pruefsumme: z.string().regex(/^[0-9a-f]{64}$/, "Keine gültige Prüfsumme.") }).optional(),
  })
  .refine(
    (a) => a.montagetermin !== undefined || a.auftragAm !== undefined || a.montiertAm !== undefined || a.dachplanung !== undefined || a.k2Excel !== undefined,
    "Es fehlt, was geändert werden soll.",
  );
export type AnlageBerichtigenEingabe = z.infer<typeof anlageBerichtigenEingabe>;

// --- „Gesehen“ ohne Lücke (seit E3, S4) ----------------------------------------------------------

/**
 * „Gesehen“ des Büros nach einem Nachtrag oder einer neuen Dachplanung
 * (bis E3c auch des Lagers am Band „geändert seit der Freigabe“). `gezeigtBis`: der
 * Zeitpunkt der jüngsten Änderung, die die Seite gezeigt hat (so, wie der
 * Server ihn geliefert hat). Ist am Server inzwischen eine jüngere da, gilt
 * „gesehen“ nicht – 409 „konflikt“, nichts geschrieben, die Seite lädt neu.
 * Warum: Vorher galt „gesehen“ bis zur Uhrzeit des Klicks, und eine Änderung
 * zwischen Laden und Klick wäre ungesehen mit abgehakt worden (DECISIONS.md,
 * 21.10., „E3, Schritt 1 gebaut“, Punkt 22).
 *
 * Woher die Seite den Zeitpunkt nimmt: aus der Besichtigung (der jüngste
 * gesendete Nachtrag oder `anlage.k2GeaendertAm`, das Jüngere).
 */
export const gesehenEingabe = z.object({
  gezeigtBis: zeitpunkt,
});
export type GesehenEingabe = z.infer<typeof gesehenEingabe>;

// --- Jahresziel (O58) ---------------------------------------------------------------------------

/** Das Ziel der Firma: so viele Anlagen in diesem Jahr. */
export const jahreszielEingabe = z.object({
  jahr: z.int().min(2020).max(2100),
  anlagen: z.int().min(0).max(100_000),
});
export type JahreszielEingabe = z.infer<typeof jahreszielEingabe>;

// --- Antworten des Servers ------------------------------------------------------------------------
// Zeitpunkte als ISO-Text, Mengen und Preise als Zahl. Nur, was die Apps
// zeigen – nie Firma, nie „angelegt von“.

/** Wer etwas getan hat – Kennung und Name, wie ihn die Leute im Büro kennen. */
export interface NutzerKurz {
  id: string;
  name: string;
}

/** Eine Frage samt Antworten (älteste zuerst), wie sie am Server liegt. */
export interface FrageAmServer {
  id: string;
  aufnahmeId: string;
  bezug: { tabelle: FrageBezug; id: string };
  text: string;
  von: NutzerKurz;
  gestelltAm: string;
  stand: FrageStand;
  /** Null, solange sie offen ist (auch nach „wieder offen“). */
  erledigtAm: string | null;
  erledigtVon: NutzerKurz | null;
  antworten: { id: string; text: string; von: NutzerKurz; geantwortetAm: string }[];
}

/**
 * Eine Materialposition der Freigabe. `ersetztAm` gesetzt: Sie galt bis
 * dahin und ist ersetzt (oder entfernt); die Nachfolgerin nennt sie in
 * `vorgaengerId`. Eine neue Position ohne Vorgängerin kam nach der Freigabe
 * dazu, wenn ihr `angelegtAm` nach der Freigabe liegt.
 */
export interface PositionAmServer {
  id: string;
  herkunft: MaterialHerkunft;
  stationId: string | null;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
  preis: number | null;
  angelegtAm: string;
  ersetztAm: string | null;
  vorgaengerId: string | null;
}

/** Ein Eintrag der Standardliste, wie das Büro ihn sieht. */
export interface StandardmaterialZeile {
  id: string;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
  art: MaterialArt;
  aktiv: boolean;
  /** Seit E3b: die Gruppe für Varianten („Kabelkanal“) – null: keine. */
  gruppe: string | null;
}

// --- Die Wege fürs Büro (seit E3, Schritt 1, Teil 2) -----------------------------------
//
//   GET   /api/crm/aufnahmen                 PruefListeAntwort
//   GET   /api/crm/aufnahme/:id              CrmAufnahme
//   GET   /api/crm/datei/:pruefsumme         die Datei (wie in der Doku-App, mit Teil-Anfragen)
//   GET   /api/crm/standardmaterial          StandardlisteAntwort
//   POST  /api/crm/standardmaterial          BueroAntwort      (standardmaterialEingabe)
//   POST  /api/crm/aufnahme/:id/freigeben    FreigebenAntwort  (freigabeEingabe)
//   POST  /api/crm/aufnahme/:id/material     MaterialAntwort   (materialEingabe)
//   POST  /api/crm/aufnahme/:id/gesehen      BueroAntwort      (gesehenEingabe – seit S4)
//   PATCH /api/crm/kunde/:id                BueroAntwort      (kundeBerichtigenEingabe)
//   PATCH /api/crm/objekt/:id                BueroAntwort      (objektBerichtigenEingabe)
//   PATCH /api/crm/anlage/:id                BueroAntwort      (anlageBerichtigenEingabe)
//   GET   /api/crm/fragen[?offen=1]          FragenAntwort
//   POST  /api/crm/frage                     BueroAntwort      (frageEingabe)
//   POST  /api/crm/antwort                   BueroAntwort      (antwortEingabe)
//   POST  /api/crm/frage/:id/stand           BueroAntwort      ({ stand })
//
// Seit Teil 3 von Schritt 1:
//
//   GET   /api/crm/uebersicht                UebersichtAntwort  (Leitung, Büro)
//   POST  /api/crm/firma/jahresziel          BueroAntwort       (jahreszielEingabe – nur die Leitung)
//   GET   /api/crm/unterlagen                UnterlagenAntwort  (Montage, Inbetriebnahme, Büro, Leitung)
//   GET   /api/crm/kunden                    KundenAntwort      (Büro, Leitung)
//   GET   /api/crm/anlage/:id                Akte               (Büro, Leitung)
//
// Wer was darf, steht in rechte.ts (je Route ein Recht); was ein Leser sieht,
// regelt der Server: Der Verkauf sieht nur, was er selbst aufgenommen hat;
// Montage und Inbetriebnahme lesen nur freigegebene Besichtigungen (für die
// Unterlage); eine Besichtigung, die noch aufgenommen wird, gibt es im CRM
// nicht. Was es für jemanden nicht gibt, ist 404 – gleich wie „gibt es gar
// nicht“.

/**
 * Eine Antwort, die kein „ok“ ist: 400 (Eingabe ungültig, ohne Grund), 404
 * (gibt es hier nicht), 409 (passt nicht zu dem, was am Server ist – mit
 * Grund wie beim Eingang der Doku-App, z. B. „konflikt“).
 */
export interface BueroAbweisung {
  ok: false;
  grund?: EintragAbweisung;
  meldung: string;
}

/** Die Antwort der schreibenden Wege – 200 heißt: genau einmal da (dasselbe noch einmal ändert nichts). */
export type BueroAntwort = { ok: true } | BueroAbweisung;

/** „Freigeben“: `schonFreigegeben` – war es schon, nichts geändert (zweimal gedrückt, zwei im Büro). */
export type FreigebenAntwort = { ok: true; schonFreigegeben?: true } | BueroAbweisung;

/**
 * „Material nach der Freigabe ändern“: `geaendert` – ob sich etwas geändert
 * hat. Eine geänderte Position bekommt am Server eine NEUE Kennung (die alte
 * bleibt als ersetzt stehen) – danach die Besichtigung neu lesen, bevor
 * weiter geändert wird.
 */
export type MaterialAntwort = { ok: true; geaendert: boolean } | BueroAbweisung;

/**
 * Warum eine Besichtigung in der Liste „zu prüfen“ auffällt – gerechnet,
 * nicht gespeichert (PLAN.md, E3, „Handwerk“):
 *  - neu: gesendet, noch nicht freigegeben, ohne Nachtrag;
 *  - nachtrag: gesendet, noch nicht freigegeben, mit gesendetem Nachtrag;
 *  - bitte-ansehen: freigegeben, aber danach kam von draußen ein Nachtrag
 *    oder eine geänderte Dachplanung – und das Büro hat seither nicht
 *    „gesehen“ gedrückt.
 */
export const PRUEF_HINWEISE = ["neu", "nachtrag", "bitte-ansehen"] as const;
export type PruefHinweis = (typeof PRUEF_HINWEISE)[number];

/** Die Dachplanung mit K2 in einem Wort: das PDF liegt da, übersprungen, oder noch nichts. */
export type K2Stand = "abgelegt" | "uebersprungen" | "offen";

/** Eine Zeile der Liste „zu prüfen“ (und aller gesendeten Besichtigungen) im CRM. */
export interface PruefZeile {
  aufnahmeId: string;
  anlageId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  /** Wer sie aufgenommen hat. */
  verkaeufer: NutzerKurz;
  /** Nie „in_aufnahme“ – das CRM kennt nur Gesendetes. */
  stand: AufnahmeStandAmServer;
  gesendetAm: string;
  freigegebenAm: string | null;
  stationenFertig: number;
  stationenGesamt: number;
  k2: K2Stand;
  /** Nachträge, die schon im Büro sind. */
  nachtraege: number;
  offeneFragen: number;
  /** Die Kiste, in der die Anlage steht (Schlüssel aus daten/kisten.json) – nach der Freigabe seit E3c „bestellung“ (vorher „lager“). */
  kiste: string;
  hinweis: PruefHinweis | null;
}

/**
 * Die Antwort auf GET /api/crm/aufnahmen: zuerst, was zu prüfen ist (neueste
 * gesendet zuerst), dann „bitte ansehen“, dann die übrigen nach der Freigabe
 * (neueste zuerst).
 */
export interface PruefListeAntwort {
  aufnahmen: PruefZeile[];
}

/**
 * Eine Besichtigung ganz, wie das Büro sie sieht – die Antwort auf GET
 * /api/crm/aufnahme/:id: alles, was die Doku-App auch bekommt
 * (`AufnahmeAmServer`, samt Fragen), dazu, wer sie aufgenommen und
 * freigegeben hat, wo die Anlage steht, das freigegebene Material und der
 * Hinweis aus der Liste.
 */
export interface CrmAufnahme extends Omit<AufnahmeAmServer, "anlage"> {
  verkaeufer: NutzerKurz;
  freigegebenAm: string | null;
  freigegebenVon: NutzerKurz | null;
  anlage: AufnahmeAmServer["anlage"] & {
    /** Schlüssel aus daten/kisten.json. */
    kiste: string;
    /** „Auftrag am“ und „montiert am“ (O58) – Kalendertage, von Hand gesetzt. */
    auftragAm: string | null;
    montiertAm: string | null;
    /** Wann die Dachplanung zuletzt geändert wurde – „Dachplanung geändert am …“. */
    k2GeaendertAm: string | null;
    /** Seit E3c (S3): der Excel-Report von K2 (Prüfsumme) – null: noch keiner; ein Server mit altem Stand schickt ihn nicht. */
    k2Excel?: string | null;
  };
  /** Alle Materialpositionen, auch ersetzte, in der Reihenfolge der Liste; leer bis zur Freigabe. */
  positionen: PositionAmServer[];
  hinweis: PruefHinweis | null;
  /** Wann das Büro zuletzt „gesehen“ gedrückt hat (nach einem Nachtrag oder einer neuen Dachplanung). */
  gesehenAm: string | null;
}

/**
 * Eine Frage mit dem, was eine Liste über sie zeigt: Kunde, Ort und die
 * Nummer der Station (aus der Station selbst, der Station des Fotos oder der
 * Notiz – null bei einer Frage an Anlage oder Besichtigung).
 */
export interface FrageMitKontext extends FrageAmServer {
  kontext: { kundeName: string; ort: string; stationNr: number | null };
}

/** Die Antwort auf GET /api/crm/fragen – älteste zuerst. */
export interface FragenAntwort {
  fragen: FrageMitKontext[];
}

/** Die Antwort auf GET /api/crm/standardmaterial: aktive zuerst, dann „immer“ vor „Auswahl“, dann nach Bezeichnung. */
export interface StandardlisteAntwort {
  eintraege: StandardmaterialZeile[];
}

// --- Die übrigen Lese-Wege fürs Büro (seit E3, Schritt 1, Teil 3) -------------------------
//
// Was für alle gilt: Das CRM kennt nur gesendete Besichtigungen – eine, die
// draußen noch aufgenommen wird, steht nirgends mit Kennung (sie gehört dem
// Verkäufer, bis er sendet); ihre Anlage, ihr Kunde und ihr Objekt gibt es
// aber schon (Kern). Kalendertage, Wochen und Jahre zählen nach Wiener Zeit
// (Europe/Vienna) – so, wie Manuel und das Büro den Kalender lesen.

/** Der Montagetermin in Kurzform: Tag „JJJJ-MM-TT“ und Text, beides null: keiner (Gernots Entscheidung 4). */
export interface Montagetermin {
  am: string | null;
  text: string | null;
}

/** Ein Kunde, wie Listen und Akte ihn zeigen. */
export interface KundeKurz {
  id: string;
  name: string;
  telefon: string | null;
  email: string | null;
  /** Seit E3b (O82): null – (noch) nicht aufgeteilt. Freiwillig: Listen brauchen es nicht. */
  vorname?: string | null;
  nachname?: string | null;
}

/** Ein Objekt, wie Listen und Akte es zeigen. `art`: Einfamilienhaus – andere Arten sind vorbereitet. */
export interface ObjektKurz {
  id: string;
  art: string;
  strasse: string;
  plz: string;
  ort: string;
}

// --- Manuels Übersicht (S2; Gernots Entscheidung 3: „Top-Qualität, kein Schein“) -------------

/**
 * Eine Anlage als Karte in ihrer Kiste. `aufnahmeId` und `stand`: die
 * jüngste gesendete Besichtigung – null, wenn es keine gibt (noch draußen in
 * Aufnahme, oder eine Anlage ohne Besichtigung). `hinweis` wie in der Liste
 * „zu prüfen“.
 *
 * Seit Gernots Augentest E3c (4.11.2026, O117: „Man soll eigentlich immer
 * dort direkt hinkommen, wo es die Kiste sagt“) trägt die Karte dazu, was
 * ihre Spalte braucht, um dorthin zu führen und zu sagen, was dort offen ist:
 * ob die Besichtigung noch draußen ist, was von K2 da ist, und die Kette.
 */
export interface KistenKarte {
  anlageId: string;
  kundeName: string;
  ort: string;
  aufnahmeId: string | null;
  stand: AufnahmeStandAmServer | null;
  hinweis: PruefHinweis | null;
  /** Der Montagetermin der Anlage – seit dem Augentest (Gernot, 21.10.2026) im Weg bei Bestellung (bis E3c: Lager) und Montage zu sehen. */
  montagetermin: Montagetermin;
  /** Wann montiert („JJJJ-MM-TT“, von Hand in der Akte, O58) – null: noch nicht. */
  montiertAm: string | null;
  /** Seit 4.11.2026: Die Besichtigung wird noch draußen aufgenommen, gesendet ist keine – die Karte sagt es und führt in die Akte. */
  draussen: boolean;
  /** Seit 4.11.2026: Was von K2 bei der Anlage liegt – die Spalte Dachplanung sagt, welche Datei fehlt (kette.ts, k2Fehlt). */
  k2: { pdf: boolean; excel: boolean };
  /** Seit 4.11.2026: die Kette der Anlage – daraus sagen Bestellung und Montage, was noch offen ist (kette.ts). */
  kette: Kette;
}

/** Eine Kiste der Übersicht: wie viele Anlagen der Firma gerade darin stehen, und die jüngsten (höchstens 8) als Karten. */
export interface KisteMitAnlagen {
  /** Schlüssel aus daten/kisten.json – alle acht, in deren Reihenfolge, auch leere. */
  kiste: string;
  anzahl: number;
  karten: KistenKarte[];
}

/** Die Kennzahlen der Übersicht – jede aus der Datenbank gezählt, keine geschätzt. */
export interface UebersichtZahlen {
  /** Seit E3c, S2: Anlagen auf „Zu tun“ – dieselbe Zahl wie in der Seitenleiste (kette.ts, aufZuTun). */
  zuTun: number;
  /** Gesendet, noch nicht freigegeben. */
  zuPruefen: number;
  /** Freigegeben, danach kam von draußen etwas – das Büro hat es noch nicht gesehen. */
  bitteAnsehen: number;
  /** Draußen begonnen, schon am Server, noch nicht gesendet. */
  inAufnahme: number;
  /** Offene Fragen an allen Besichtigungen der Firma. */
  offeneFragen: number;
  /** Gesendete Nachträge der letzten 30 Tage. */
  nachtraege30Tage: number;
  /** Seit wann die älteste zu prüfende wartet (gesendet am) – null: keine wartet. */
  wartetLaengstSeit: string | null;
  /**
   * Seit Gernots Augentest E3c (4.11.2026, O115): die Kennung dieser ältesten
   * Besichtigung – die Kachel „Längste Wartezeit“ führt gleich zu ihr, nicht
   * nach „Zu tun“; null: keine wartet.
   */
  wartetLaengstAufnahmeId: string | null;
}

/** Gesendet und freigegeben in einer Kalenderwoche (Montag bis Sonntag, Wiener Zeit). */
export interface WochenZahl {
  /** Der Montag „JJJJ-MM-TT“. */
  wocheAb: string;
  gesendet: number;
  freigegeben: number;
}

/** Gesendet und freigegeben in einem Kalendermonat (Wiener Zeit). */
export interface MonatsZahl {
  /** „JJJJ-MM“ */
  monat: string;
  gesendet: number;
  freigegeben: number;
}

/** Die Zeit von „gesendet“ bis „freigegeben“ – über die in den letzten 90 Tagen freigegebenen. */
export interface Durchlauf {
  anzahl: number;
  /** Der Median in Stunden, eine Nachkommastelle – null, wenn keine. */
  medianStunden: number | null;
}

/**
 * Manuels Zähler „verkauft – montiert – Ziel“ (O58) für das laufende Jahr:
 * Anlagen mit „Auftrag am“ bzw. „montiert am“ in diesem Jahr. Die beiden
 * Tage setzen Büro oder Leitung in der Akte von Hand, bis die Kisten Sales
 * und Montage sie füllen – die Oberfläche sagt das dazu. `ziel`: das
 * Jahresziel der Firma, null: keines gesetzt.
 */
export interface JahresZaehler {
  jahr: number;
  verkauft: number;
  montiert: number;
  ziel: number | null;
}

/** Die Antwort auf GET /api/crm/uebersicht – die Startseite des CRM für Leitung und Büro. */
/**
 * Eine Aufgaben-Spalte im Weg (seit E3b, O70 – Gernot: „ständig sehen, wo
 * sie fehlen und wo sie erledigt sind“): die Anlagen mit gesendeter
 * Besichtigung, noch nicht montiert. „erledigt“ ergibt sich aus dem, was
 * ohnehin passiert – Termin: der Montagetermin steht an der Anlage;
 * Dachplanung: der K2-Plan (PDF) und seit Gernots Augentest E3c (4.11.2026)
 * auch die K2-Liste (Excel) liegen bei der Anlage – wie in der Kiste
 * Dachplanung (kette.ts, k2Fehlt). `fehlt`: die ältesten zuerst, höchstens 8
 * als Karten; `fehltAnzahl` zählt alle.
 */
export interface AufgabenSpalte {
  fehlt: KistenKarte[];
  fehltAnzahl: number;
  erledigt: number;
}

export interface UebersichtAntwort {
  kisten: KisteMitAnlagen[];
  /** Seit E3b (O70): Termin und Dachplanung als Aufgaben – die zwei Kisten, durch die keine Anlage „wandert“. */
  aufgaben: { termin: AufgabenSpalte; dachplanung: AufgabenSpalte };
  zahlen: UebersichtZahlen;
  /** Die letzten 8 Kalenderwochen, die laufende zuletzt; Wochen ohne Werte mit 0. */
  jeWoche: WochenZahl[];
  /** Die letzten 6 Monate, der laufende zuletzt; Monate ohne Werte mit 0. */
  jeMonat: MonatsZahl[];
  durchlauf: Durchlauf;
  zaehler: JahresZaehler;
  /** Wann gezählt wurde (ISO). */
  stand: string;
}

// --- Unterlagen (S4) ---------------------------------------------------------------------------

/** Eine Unterlage für den Monteur in der Liste – die Unterlage selbst ist die Besichtigung ganz (GET /api/crm/aufnahme/:id, mit Material). */
export interface UnterlagenZeile {
  aufnahmeId: string;
  anlageId: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  freigegebenAm: string;
  montagetermin: Montagetermin;
}

/**
 * Die Antwort auf GET /api/crm/unterlagen – alle freigegebenen: zuerst die
 * kommenden Montagetermine (ab heute, der nächste zuerst), dann die ohne
 * Termin (neueste Freigabe zuerst), zuletzt die vergangenen (jüngste zuerst).
 */
export interface UnterlagenAntwort {
  unterlagen: UnterlagenZeile[];
}

// --- Kunden und Anlagen, die Akte (S5) ------------------------------------------------------------

/** Die jüngste gesendete Besichtigung einer Anlage, in einer Zeile. */
export interface AufnahmeKurz {
  id: string;
  stand: AufnahmeStandAmServer;
  gesendetAm: string;
  freigegebenAm: string | null;
}

/** Eine Zeile der Liste „Kunden & Anlagen“ – jede Anlage der Firma, auch eine ohne Besichtigung. */
export interface KundenZeile {
  anlageId: string;
  kunde: KundeKurz;
  objekt: ObjektKurz;
  /** Schlüssel aus daten/produktlinien.json. */
  produktlinie: string;
  /** Schlüssel aus daten/kisten.json. */
  kiste: string;
  aufnahme: AufnahmeKurz | null;
  hinweis: PruefHinweis | null;
  /** Offene Fragen an allen Besichtigungen dieser Anlage. */
  offeneFragen: number;
  /**
   * Seit Gernots Augentest E3c (O115): wann an der Anlage zuletzt etwas
   * geschah – der jüngste Eintrag in ihrem Verlauf, derselbe wie oben im
   * Verlauf der Akte (ISO); null: noch keiner. Danach sortiert „zuletzt
   * bearbeitet“ in „Kunden & Anlagen“.
   */
  zuletztAm: string | null;
}

/** Die Antwort auf GET /api/crm/kunden – nach Kundenname (deutsche Sortierung); „zuletzt bearbeitet“ sortiert die Seite selbst (zuletztAm). */
export interface KundenAntwort {
  anlagen: KundenZeile[];
}

/** Eine Besichtigung in der Akte – nur Anzahlen; ganz gibt es sie über GET /api/crm/aufnahme/:id. */
export interface AkteAufnahme {
  id: string;
  stand: AufnahmeStandAmServer;
  begonnenAm: string;
  gesendetAm: string;
  freigegebenAm: string | null;
  verkaeufer: NutzerKurz;
  stationenFertig: number;
  stationenGesamt: number;
  fotos: number;
  /** Notizen mit Text – eine leere Notiz, die nur ein Sprachmemo trägt, zählt nicht. */
  notizen: number;
  memos: number;
  laengen: number;
  /** Seit E3b: was der Verkäufer draußen als Material angegeben hat (Zeilen). */
  material: number;
  /** Nachträge, die schon im Büro sind. */
  nachtraege: number;
}

/**
 * Ein Eintrag im Verlauf der Akte: wer, wann, was, an welchem Datensatz –
 * mehr steht im Verlauf nicht (nie Inhalte). `wer` und `geraet` null: der
 * Zugang oder das Gerät ist nicht mehr da (der Verlauf bleibt).
 */
export interface AkteVerlaufEintrag {
  wann: string;
  /** Die Art aus server/src/verlauf.ts („gesendet“, „freigegeben“, „gefragt“ …). */
  was: string;
  /** kunde, objekt, anlage, aufnahme, nachtrag, materialliste, frage, antwort */
  tabelle: string;
  datensatzId: string | null;
  wer: NutzerKurz | null;
  /** Der Name des Geräts („iPhone Verkauf“). */
  geraet: string | null;
}

/** Die Akte einer Anlage – die Antwort auf GET /api/crm/anlage/:id. */
export interface Akte {
  kunde: KundeKurz;
  objekt: ObjektKurz;
  anlage: {
    id: string;
    produktlinie: string;
    kiste: string;
    montagetermin: Montagetermin;
    /** „Auftrag am“ und „montiert am“ (O58) – Kalendertage, von Hand gesetzt. */
    auftragAm: string | null;
    montiertAm: string | null;
    /** Die Dachplanung: das PDF (Prüfsumme) oder übersprungen, und wann sie zuletzt geändert wurde. */
    k2: { pruefsumme: string | null; uebersprungen: boolean; geaendertAm: string | null };
  };
  /** Seit E3c, S2: die acht Schritte dieser Anlage – die Leiste oben in der Akte (kette.ts). */
  kette: Kette;
  /** Seit E3c, S3 und S4: Bestellung, Dachpartie, Bodenpartie – was die drei Karten brauchen (ablauf-vertrag.ts). */
  ablauf: AkteAblauf;
  /** Die gesendeten Besichtigungen dieser Anlage, die jüngste zuerst. */
  aufnahmen: AkteAufnahme[];
  /** Ist eine ihrer Besichtigungen freigegeben (gibt es eine Materialliste)? */
  materialFreigegeben: boolean;
  /** Die Fragen an dieser Anlage und ihren Besichtigungen – älteste zuerst. */
  fragen: FrageMitKontext[];
  /** Seit E3c, S6: was „Anlage löschen“ mitnähme – für den Satz in der Rückfrage (nur die Leitung sieht den Knopf). */
  loeschen: LoeschFolgen;
  /**
   * Der Verlauf von Kunde, Objekt, Anlage, den Besichtigungen, ihren
   * Nachträgen, ihrer Materialliste und ihren Fragen und Antworten –
   * neueste zuerst, höchstens 300. Die Einzelstücke einer Besichtigung
   * (Station, Foto, Notiz, Memo, Länge) stehen nicht darin.
   */
  verlauf: AkteVerlaufEintrag[];
}
